import { DOCUMENT, isPlatformBrowser, CommonModule } from '@angular/common';
import {
  Attribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef, Inject,
  Input,
  OnInit,
  Optional,
  PLATFORM_ID
} from '@angular/core';

import { IrisTokenParserService } from '@sondermind/iris/shared';

import { IconColors } from './enums/icon-colors.enum';
import { IconSizes } from './enums/icon-sizes.enum';
import { IconUsageTypes } from './enums/icon-usage-types.enum';
import { IrisIconRegistry } from './icon-registry.service';

@Component({
  selector: 'iris-icon',
  templateUrl: './icon.component.html',
  styleUrls: ['./icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule],
  providers: [IrisIconRegistry],
  host: {
    class: 'iris-icon',
    /* eslint-disable @typescript-eslint/naming-convention */
    '[class.iris-icon--brand-1]': 'color === colorOptions.BRAND1',
    '[class.iris-icon--brand-2]': 'color === colorOptions.BRAND2',
    '[class.iris-icon--white]': 'color === colorOptions.WHITE',
    '[class.iris-icon--success]': 'color === colorOptions.SUCCESS',
    '[class.iris-icon--warning]': 'color === colorOptions.WARNING',
    '[class.iris-icon--danger]': 'color === colorOptions.DANGER',
    '[class.iris-icon--hover]': 'color === colorOptions.HOVER',
    '[class.iris-icon--error]': 'color === colorOptions.ERROR',
    '[class.iris-icon--token-color]': 'colorToken !== ""',
    '[class.iris-icon--small]': 'size === sizeOptions.SM',
    '[class.iris-icon--medium]': 'size === sizeOptions.MD',
    '[class.iris-icon--large]': 'size === sizeOptions.LG',
    '[attr.aria-label]': 'ariaLabel'
    /* eslint-enable @typescript-eslint/naming-convention */
  }
})
export class IrisIconComponent implements OnInit {
  private svgIcon!: SVGElement;
  private colorOptions = IconColors;
  private sizeOptions = IconSizes;
  private usageTypes = IconUsageTypes;

  @Input() color: IconColors = this.colorOptions.BRAND1;
  @Input() size: IconSizes = this.sizeOptions.MD;
  @Input() ariaLabel!: string;
  @Input() usage: IconUsageTypes = this.usageTypes.DECORATIVE;
  @Input() colorToken: string = '';

  @Input() set name(iconName: string) {
    if (this.svgIcon) {
      this.element.nativeElement.removeChild(this.svgIcon);
    }

    // Gather Icon data from registry
    const svgData = this.irisIconRegistry.getIcon(iconName);

    if (isPlatformBrowser(this.platformId)) {
      // Create SVG Element
      this.svgIcon = this.svgElementFromString(svgData || '');
      // Append SVG Element to the DOM
      this.element.nativeElement.appendChild(this.svgIcon);
    }
  }

  constructor(
    private element: ElementRef<HTMLElement>,
    private irisIconRegistry: IrisIconRegistry,
    private irisTokenParserService: IrisTokenParserService,
    @Attribute('aria-hidden') ariaHidden: string,
    @Optional() @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    // If the user has not explicitly set aria-hidden, mark the icon as hidden.
    if (!ariaHidden) {
      element.nativeElement.setAttribute('aria-hidden', 'true');
    }
  }

  ngOnInit(): void {
    // Handle usage type
    if (this.usage === IconUsageTypes.STAND_ALONE) {
      this.element.nativeElement.setAttribute('aria-hidden', 'false');
      this.element.nativeElement.setAttribute('role', 'img');
    }

    this.irisTokenParserService.setColorFromToken(this.colorToken, 'icon-token', this.element);
  }

  private svgElementFromString(svgContent: string): SVGElement {
    const div = this.document.createElement('DIV');
    div.innerHTML = svgContent;

    // Return svg element if available, if not, create one.
    return div.querySelector('svg') || this.document.createElementNS('http://www.w3.org/2000/svg', 'path');
  }
}
